<template>
  <v-dialog
    v-model="dialogProxy"
    fullscreen
    scrollable
    persistent
  >
    <v-card class="d-flex flex-column">
      <v-toolbar
        flat
        color="white"
        class="px-5 elevation-1 toolbar toolbar--compact special-rates-toolbar"
        height="64"
      >
        <div class="special-rates-toolbar__info">
          <div class="text-h5 font-weight-bold">
            Tarifas Especiais
          </div>
          <div
            v-if="category && category.description"
            class="subtitle-2 grey--text"
          >
            {{ category.description }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          icon
          class="toolbar-btn"
          aria-label="Fechar tarifas especiais"
          @click="handleClose"
        >
          <v-icon color="error">
            {{ icons.mdiClose }}
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider></v-divider>

      <v-card-text class="pa-6 overflow-auto">
        <v-row>
          <v-col
            cols="12"
            md="9"
          >
            <v-alert
              type="info"
              outlined
              dense
              class="mb-6"
            >
              Defina valores diretamente no calendário. Clique em um dia para editar rapidamente ou utilize o formulário lateral para aplicar em períodos maiores.
            </v-alert>

            <v-card
              flat
              class="pa-4 calendar-wrapper"
              outlined
            >
              <div class="calendar-header d-flex justify-space-between align-center mb-4">
                <div>
                  <div class="text-h5 font-weight-bold text-capitalize">
                    {{ calendarTitle }}
                  </div>
                  <div class="text-caption grey--text">
                    Visualize e ajuste as tarifas especiais do período
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-btn
                    icon
                    class="toolbar-btn mr-1"
                    aria-label="Mês anterior"
                    @click="calendarPrev"
                  >
                    <v-icon color="primary">
                      {{ icons.mdiChevronLeft }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    class="toolbar-btn mr-3"
                    aria-label="Próximo mês"
                    @click="calendarNext"
                  >
                    <v-icon color="primary">
                      {{ icons.mdiChevronRight }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    text
                    small
                    color="primary"
                    class="mr-2"
                    @click="calendarToday"
                  >
                    Hoje
                  </v-btn>
                  <v-menu
                    v-model="monthMenu"
                    offset-y
                    :close-on-content-click="false"
                    max-width="320"
                    min-width="320"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        text
                        small
                        class="ml-2"
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                      >
                        Escolher mês
                        <v-icon
                          right
                          small
                        >
                          {{ icons.mdiCalendar }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-date-picker
                      v-model="monthPickerValue"
                      type="month"
                      color="primary"
                      @input="selectMonth"
                    ></v-date-picker>
                  </v-menu>
                </div>
              </div>

              <v-calendar
                ref="specialCalendar"
                v-model="calendarFocus"
                type="month"
                color="primary"
                :short-weekdays="true"
                :events="calendarEvents"
                :event-color="getEventColor"
                :event-margin-bottom="8"
                :day-format="dayNumberFormat"
                :day-props="dayProps"
                @click:date="handleDateClick"
                @click:event="handleEventClick"
              >
                <template v-slot:event="{ event }">
                  <div class="tariff-event">
                    <div
                      class="tariff-event__pill"
                      :class="{ 'tariff-event--selected': isDateSelected(event.start) }"
                    >
                      {{ event.name }}
                    </div>
                    <div class="tariff-event__actions">
                      <v-btn
                        icon
                        x-small
                        color="error"
                        aria-label="Remover tarifa especial"
                        :loading="deletingRateId === event.data.id"
                        :disabled="saving"
                        @click.stop="deleteRate(event.data.id)"
                      >
                        <v-icon small>
                          {{ icons.mdiClose }}
                        </v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>
              </v-calendar>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="3"
          >
            <v-card
              outlined
              class="mb-4"
            >
              <v-card-title class="subtitle-1 font-weight-bold">
                Tarifa do dia selecionado
              </v-card-title>
              <v-card-text>
                <div
                  v-if="selectedDate"
                  class="mb-4"
                >
                  <div class="text-caption grey--text text--darken-1">
                    Data selecionada
                  </div>
                  <div class="text-subtitle-1 font-weight-medium text-capitalize">
                    {{ formatDateHuman(selectedDate) }}
                  </div>
                </div>
                <div
                  v-else
                  class="mb-4 grey--text"
                >
                  Clique em uma data do calendário para criar ou editar rapidamente.
                </div>
                <div class="caption grey--text text--darken-1 mb-2">
                  Preencha somente os valores que mudam. Campos vazios herdam o tarifário padrão.
                </div>
                <vuetify-money
                  v-if="!isOccupancyPricing"
                  v-model="singleRateForm.price"
                  label="Valor único (opcional)"
                  outlined
                  dense
                  :disabled="!selectedDate || saving"
                />
                <OccupancyPricingEditor
                  :value="singleRateForm"
                  :child-policies="childPolicies"
                  :default-child-rates="category.child_rates || []"
                  :show-mode="false"
                  :show-occupancy="isOccupancyPricing"
                  sparse
                  compact
                />
                <div class="d-flex flex-wrap justify-end gap-2 mt-2">
                  <v-btn
                    text
                    small
                    color="grey"
                    :disabled="!selectedDate"
                    @click="clearSelectedDate"
                  >
                    Limpar
                  </v-btn>
                  <v-btn
                    color="primary"
                    small
                    :disabled="!selectedDate || saving"
                    :loading="saving && singleEditInProgress"
                    @click="saveSingleRate"
                  >
                    Salvar dia
                  </v-btn>
                  <v-btn
                    color="error"
                    small
                    outlined
                    :disabled="!selectedDate || !hasSpecialRate(selectedDate)"
                    @click="deleteSpecialRateByDate(selectedDate)"
                  >
                    Remover
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <v-card outlined>
              <v-card-title class="subtitle-1 font-weight-bold">
                Aplicar em período/datas
              </v-card-title>
              <v-card-text>
                <div class="caption grey--text text--darken-1 mb-2">
                  Preencha somente os valores que mudam. Campos vazios herdam o tarifário padrão.
                </div>
                <vuetify-money
                  v-if="!isOccupancyPricing"
                  v-model="bulkForm.price"
                  label="Valor único (opcional)"
                  outlined
                  dense
                  :disabled="saving"
                />
                <OccupancyPricingEditor
                  :value="bulkForm"
                  :child-policies="childPolicies"
                  :default-child-rates="category.child_rates || []"
                  :show-mode="false"
                  :show-occupancy="isOccupancyPricing"
                  sparse
                  compact
                  class="mb-4"
                />

                <div class="mb-2 font-weight-medium mt-4">
                  Modo de seleção
                </div>
                <v-btn-toggle
                  v-model="bulkForm.mode"
                  mandatory
                  color="primary"
                  dense
                  rounded
                  class="mb-4"
                >
                  <v-btn value="range">
                    Período contínuo
                  </v-btn>
                  <v-btn value="multiple">
                    Datas específicas
                  </v-btn>
                </v-btn-toggle>

                <v-slide-y-transition>
                  <div v-if="bulkForm.mode === 'range'">
                    <v-menu
                      v-model="rangePickerMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="rangeLabel"
                          label="Período"
                          readonly
                          outlined
                          dense
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="bulkForm.range"
                        range
                        color="primary"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="rangePickerMenu = false"
                        >
                          Concluir
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </div>
                  <div v-else>
                    <v-menu
                      v-model="datesPickerMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      max-width="320px"
                      min-width="320px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="datesLabel"
                          label="Datas selecionadas"
                          readonly
                          outlined
                          dense
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="bulkForm.dates"
                        multiple
                        color="primary"
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="datesPickerMenu = false"
                        >
                          Concluir
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </div>
                </v-slide-y-transition>

                <p class="text-caption grey--text mt-4">
                  Use o calendário para visualizar as tarifas já aplicadas e o seletor acima para definir novos períodos ou datas específicas.
                </p>
                <v-btn
                  block
                  color="primary"
                  :loading="saving && !singleEditInProgress"
                  :disabled="saving"
                  @click="submitBulkRate"
                >
                  Aplicar tarifa
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiCalendar, mdiChevronLeft, mdiChevronRight, mdiClose,
} from '@mdi/js'
import { mapActions } from 'vuex'
import OccupancyPricingEditor from '@/components/OccupancyPricingEditor.vue'

export default {
  name: 'RatePlanSpecialRatesModal',
  components: { OccupancyPricingEditor },

  props: {
    value: { type: Boolean, default: false },
    ratePlanId: { type: Number, default: null },
    category: { type: Object, default: () => ({ id: null, description: '' }) },
    childPolicies: { type: Array, default: () => [] },
  },

  data: () => ({
    specialRates: [],
    loading: false,
    saving: false,
    deletingRateId: null,
    singleEditInProgress: false,
    selectedDate: null,
    calendarFocus: new Date().toISOString().substr(0, 10),
    monthMenu: false,
    monthPickerValue: null,
    rangePickerMenu: false,
    datesPickerMenu: false,
    singleRateForm: { price: null, occupancy_rates: [], child_rates: [] },
    bulkForm: {
      mode: 'range',
      price: null,
      occupancy_rates: [],
      child_rates: [],
      range: [],
      dates: [],
    },
    icons: {
      mdiCalendar, mdiChevronLeft, mdiChevronRight, mdiClose,
    },
  }),

  computed: {
    dialogProxy: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },
    specialRateMap() {
      return this.specialRates.reduce((acc, rate) => {
        if (rate && rate.date) acc[rate.date] = rate

        return acc
      }, {})
    },
    calendarEvents() {
      return this.specialRates.map(rate => ({
        name: this.specialRateLabel(rate),
        start: rate.date,
        end: rate.date,
        color: 'light-blue lighten-4',
        data: rate,
      }))
    },
    calendarTitle() {
      if (!this.calendarFocus) return ''
      const date = new Date(`${this.calendarFocus}T00:00:00`)

      return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    },
    rangeLabel() {
      const range = this.bulkForm.range || []
      const start = Array.isArray(range) ? range[0] : range?.start
      const end = Array.isArray(range) ? range[1] : range?.end
      if (!start || !end) return ''

      return `${new Date(`${start}T00:00:00`).toLocaleDateString('pt-BR')} - ${new Date(`${end}T00:00:00`).toLocaleDateString('pt-BR')}`
    },
    datesLabel() {
      if (!this.bulkForm.dates || !this.bulkForm.dates.length) return ''

      return this.bulkForm.dates.map(d => new Date(`${d}T00:00:00`).toLocaleDateString('pt-BR')).join(', ')
    },
    isOccupancyPricing() {
      return this.category?.pricing_mode === 'occupancy'
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.loadRates()
      } else {
        this.resetState()
      }
    },
    monthMenu(value) {
      if (value) {
        this.monthPickerValue = this.calendarFocus ? this.calendarFocus.slice(0, 7) : null
      }
    },
  },

  methods: {
    ...mapActions('rate_plan', {
      listSpecialRates: 'listSpecialRates',
      storeSpecialRates: 'storeSpecialRates',
      removeSpecialRateAction: 'deleteSpecialRate',
    }),

    async loadRates() {
      if (!this.ratePlanId || !this.category?.id) return
      this.loading = true
      try {
        const res = await this.listSpecialRates({ ratePlanId: this.ratePlanId, categoryId: this.category.id })
        this.specialRates = res.data.data.rates || []
        if (this.specialRates.length) {
          this.calendarFocus = this.specialRates[0].date
          this.monthPickerValue = this.calendarFocus.slice(0, 7)
        }
        await this.$nextTick()
        this.selectedDate = null
        this.singleRateForm = this.createOverrideForm()
      } catch (e) {
        this.$toast.error('Erro ao carregar tarifas especiais')
      } finally {
        this.loading = false
      }
    },

    resetState() {
      this.specialRates = []
      this.selectedDate = null
      this.singleRateForm = this.createOverrideForm()
      this.bulkForm = {
        mode: 'range', ...this.createOverrideForm(), range: [], dates: [],
      }
      this.calendarFocus = new Date().toISOString().substr(0, 10)
      this.deletingRateId = null
      this.saving = false
      this.singleEditInProgress = false
    },

    handleClose() {
      this.$emit('input', false)
      this.$emit('updated')
      this.resetState()
    },

    normalizeDateValue(value) {
      if (!value) return null
      if (typeof value === 'string' && value.length >= 10) return value.slice(0, 10)
      const parsed = new Date(value)

      return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10)
    },

    hasSpecialRate(date) {
      const key = this.normalizeDateValue(date)

      return Boolean(key && this.specialRateMap[key])
    },

    isDateSelected(date) {
      const key = this.normalizeDateValue(date)

      return Boolean(this.selectedDate && key === this.selectedDate)
    },

    handleDateClick(payload) {
      if (!payload) return
      const targetDate = payload.date || payload.day?.date
      if (!targetDate) return
      const normalized = this.normalizeDateValue(targetDate)
      if (!normalized) return
      this.selectedDate = normalized
      const rate = this.specialRateMap[normalized]
      this.singleRateForm = this.createOverrideForm(rate)
    },

    handleEventClick({ event }) {
      if (!event) return
      const normalized = this.normalizeDateValue(event.start)
      if (!normalized) return
      this.selectedDate = normalized
      const rate = this.specialRateMap[normalized]
      this.singleRateForm = this.createOverrideForm(rate)
    },

    clearSelectedDate() {
      this.selectedDate = null
      this.singleRateForm = this.createOverrideForm()
    },

    formatDateHuman(date) {
      const normalized = this.normalizeDateValue(date)
      if (!normalized) return ''
      const parsed = new Date(`${normalized}T00:00:00`)
      if (Number.isNaN(parsed.getTime())) return normalized

      return parsed.toLocaleDateString('pt-BR', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
      })
    },

    getEventColor() {
      return 'light-blue lighten-4'
    },

    dayNumberFormat({ day }) {
      return day
    },

    dayProps({ date, outside }) {
      return {
        class: [
          outside ? 'outside-day' : '',
          this.isDateSelected(date) ? 'selected-day' : '',
        ].filter(Boolean).join(' '),
      }
    },

    calendarPrev() {
      this.adjustCalendarMonth(-1)
    },

    calendarNext() {
      this.adjustCalendarMonth(1)
    },

    calendarToday() {
      this.calendarFocus = new Date().toISOString().substr(0, 10)
      this.monthPickerValue = this.calendarFocus.slice(0, 7)
    },

    selectMonth(value) {
      const normalized = this.normalizeDateValue(value)
      if (normalized) this.calendarFocus = normalized
      this.monthMenu = false
    },

    adjustCalendarMonth(offset) {
      const base = this.normalizeDateValue(this.calendarFocus) || new Date().toISOString().slice(0, 10)
      const date = new Date(`${base}T00:00:00`)
      date.setUTCDate(1)
      date.setUTCMonth(date.getUTCMonth() + offset)
      this.calendarFocus = date.toISOString().slice(0, 10)
      this.monthPickerValue = this.calendarFocus.slice(0, 7)
    },

    async saveSingleRate() {
      if (!this.selectedDate) {
        this.$toast.error('Selecione uma data no calendário')

        return
      }
      const payload = this.buildOverridePayload(this.singleRateForm)
      if (!this.hasOverrides(payload)) {
        this.$toast.error('Informe ao menos um valor para substituir')

        return
      }
      this.singleEditInProgress = true
      this.saving = true
      try {
        await this.storeSpecialRates({
          ratePlanId: this.ratePlanId,
          categoryId: this.category.id,
          ...payload,
          dates: [this.selectedDate],
        })
        this.$toast.success('Tarifa diária salva com sucesso')
        await this.loadRates()
      } catch (e) {
        this.$toast.error('Erro ao salvar tarifa do dia')
      } finally {
        this.singleEditInProgress = false
        this.saving = false
      }
    },

    extractBulkDates() {
      if (this.bulkForm.mode === 'multiple') {
        return (this.bulkForm.dates || []).map(d => this.normalizeDateValue(d)).filter(Boolean).sort()
      }
      const range = this.bulkForm.range || []
      const start = Array.isArray(range) ? range[0] : range?.start
      const end = Array.isArray(range) ? range[1] : range?.end
      if (!start || !end) return []

      let startDate = new Date(`${start}T00:00:00Z`)
      let endDate = new Date(`${end}T00:00:00Z`)
      if (startDate > endDate) { const tmp = startDate; startDate = endDate; endDate = tmp }

      const dates = []
      const current = new Date(startDate)
      while (current <= endDate) {
        dates.push(current.toISOString().slice(0, 10))
        current.setUTCDate(current.getUTCDate() + 1)
      }

      return dates
    },

    async submitBulkRate() {
      const payload = this.buildOverridePayload(this.bulkForm)
      if (!this.hasOverrides(payload)) {
        this.$toast.error('Informe ao menos um valor para substituir')

        return
      }
      const dates = this.extractBulkDates()
      if (!dates.length) {
        this.$toast.error('Selecione ao menos uma data')

        return
      }
      this.singleEditInProgress = false
      this.saving = true
      try {
        await this.storeSpecialRates({
          ratePlanId: this.ratePlanId,
          categoryId: this.category.id,
          ...payload,
          dates,
        })
        this.$toast.success('Tarifa especial aplicada com sucesso')
        await this.loadRates()
        this.bulkForm = {
          mode: this.bulkForm.mode, ...this.createOverrideForm(), range: [], dates: [],
        }
      } catch (e) {
        this.$toast.error('Erro ao salvar tarifa especial')
      } finally {
        this.saving = false
      }
    },

    async deleteRate(rateId) {
      this.deletingRateId = rateId
      try {
        await this.removeSpecialRateAction({
          ratePlanId: this.ratePlanId,
          categoryId: this.category.id,
          rateId,
        })
        this.specialRates = this.specialRates.filter(r => r.id !== rateId)
        const rate = Object.values(this.specialRateMap).find(r => r.id === rateId)
        if (rate && this.selectedDate === rate.date) this.singleRateForm = this.createOverrideForm()
        this.$toast.success('Tarifa especial removida')
      } catch (e) {
        this.$toast.error('Erro ao remover tarifa especial')
      } finally {
        this.deletingRateId = null
      }
    },

    deleteSpecialRateByDate(date) {
      const key = this.normalizeDateValue(date)
      const rate = key ? this.specialRateMap[key] : null
      if (!rate) {
        this.$toast.info('Nenhuma tarifa especial cadastrada para esta data')

        return
      }
      this.deleteRate(rate.id)
    },
    specialRateLabel(rate) {
      const occupancyValues = (rate?.occupancy_rates || [])
        .map(occupancyRate => Number(occupancyRate.value))
        .filter(value => value > 0)
      if (this.isOccupancyPricing && occupancyValues.length) {
        return `A partir de R$ ${Math.min(...occupancyValues).toFixed(2)}`
      }
      const price = rate?.value ?? rate?.price
      if (!this.isOccupancyPricing && Number(price) > 0) return `R$ ${Number(price).toFixed(2)}`
      const childValues = (rate?.child_rates || [])
        .map(childRate => Number(childRate.value))
        .filter(value => value > 0)

      return childValues.length ? `Adicional R$ ${Math.min(...childValues).toFixed(2)}` : 'Tarifa especial'
    },
    createOverrideForm(rate = null) {
      const occupancyOverrides = rate?.occupancy_rates || []
      const childOverrides = rate?.child_rates || []

      return {
        price: rate?.value ?? rate?.price ?? null,
        occupancy_rates: (this.category?.occupancy_rates || []).map(base => {
          const override = occupancyOverrides.find(item => Number(item.occupancy) === Number(base.occupancy)) || {}

          return {
            name: base.name,
            occupancy: base.occupancy,
            adults: base.adults,
            value: override.value ?? null,
            weekend_value: override.weekend_value ?? null,
          }
        }),
        child_rates: this.childPolicies
          .filter(policy => policy.charge_type === 'partial' && policy.id)
          .map(policy => {
            const override = childOverrides.find(item => Number(item.policy_id) === Number(policy.id))

            return { policy_id: policy.id, value: override?.value ?? null }
          }),
      }
    },
    buildOverridePayload(form) {
      const price = form.price === null || form.price === '' ? null : Number(form.price)
      const occupancyRates = (form.occupancy_rates || [])
        .filter(rate => rate.value !== null || rate.weekend_value !== null)
        .map(rate => ({
          name: rate.name,
          occupancy: rate.occupancy,
          adults: rate.adults,
          value: rate.value === null || rate.value === '' ? null : Number(rate.value),
          weekend_value: rate.weekend_value === null || rate.weekend_value === '' ? null : Number(rate.weekend_value),
        }))
      const childRates = (form.child_rates || [])
        .filter(rate => rate.value !== null && rate.value !== '')
        .map(rate => ({ policy_id: rate.policy_id, value: Number(rate.value) }))

      return { price, occupancy_rates: occupancyRates, child_rates: childRates }
    },
    hasOverrides(payload) {
      return payload.price !== null || payload.occupancy_rates.length > 0 || payload.child_rates.length > 0
    },
  },
}
</script>

<style scoped>
.calendar-wrapper {
  background-color: #fff;
  min-height: 540px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tariff-event {
  display: flex;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
}

.tariff-event__pill {
  flex: 1;
  background: linear-gradient(90deg, #e3f2fd, #f1f8ff);
  border-radius: 8px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #0d47a1;
  border: 1px solid rgba(33, 150, 243, 0.2);
  box-shadow: 0 4px 12px rgba(13, 71, 161, 0.08);
}

.tariff-event__pill.tariff-event--selected {
  border-color: rgba(63, 81, 181, 0.7);
  box-shadow: 0 0 0 1px rgba(63, 81, 181, 0.2);
}

.tariff-event__actions {
  display: flex;
  align-items: center;
}

.calendar-wrapper ::v-deep .v-calendar-weekly__day-label,
.calendar-wrapper ::v-deep .v-calendar-weekly__day {
  align-items: flex-start;
}

.calendar-wrapper ::v-deep .outside-day {
  opacity: 0.35;
}

.calendar-wrapper ::v-deep .selected-day .v-calendar-weekly__day-interval {
  border-radius: 12px;
  background: rgba(63, 81, 181, 0.08);
}

.calendar-wrapper ::v-deep .v-calendar-weekly {
  flex: 1;
}

.calendar-wrapper ::v-deep .v-event {
  min-height: 34px;
  width: 100%;
}

.calendar-wrapper ::v-deep .v-event > div {
  height: auto !important;
}

.calendar-header .v-btn {
  text-transform: none;
}

.toolbar .toolbar-btn {
  color: inherit;
}

.toolbar--compact {
  min-height: 64px;
}

.special-rates-toolbar {
  padding-top: 0;
  padding-bottom: 0;
}

.special-rates-toolbar ::v-deep .v-toolbar__content {
  height: 64px !important;
}

.special-rates-toolbar__info {
  padding: 0;
}
</style>
